import { clsx } from 'clsx';
import Skeleton, { TextSkeleton } from '@components/ui/Skeleton';

interface ContentSkeletonProps {
  className?: string;
  hasHeader?: boolean;
  hasImage?: boolean;
  contentLines?: number;
  sections?: number;
}

/**
 * Full page content skeleton with header and multiple sections
 */
export function ContentSkeleton({
  className,
  hasHeader = true,
  hasImage = false,
  contentLines = 5,
  sections = 1,
}: ContentSkeletonProps) {
  return (
    <div className={clsx('py-12 bg-slate-950 min-h-screen', className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasHeader && (
          <div className="mb-10">
            <Skeleton variant="text" width={320} height={40} className="mb-4" />
            <Skeleton variant="text" width="60%" height={24} />
          </div>
        )}

        {hasImage && (
          <Skeleton
            variant="rectangular"
            height={300}
            className="mb-8 rounded-xl"
          />
        )}

        {Array.from({ length: sections }).map((_, sectionIndex) => (
          <div
            key={sectionIndex}
            className={clsx(sectionIndex > 0 && 'mt-10')}
          >
            {sectionIndex > 0 && (
              <Skeleton
                variant="text"
                width={200}
                height={28}
                className="mb-4"
              />
            )}
            <TextSkeleton lines={contentLines} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Hero section skeleton for homepage
 */
export function HeroSkeleton() {
  return (
    <section className="relative bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 py-20 lg:py-32 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          {/* Badge */}
          <Skeleton
            variant="text"
            width={280}
            height={32}
            className="mx-auto mb-8 rounded-full"
          />

          {/* Title */}
          <div className="space-y-2 mb-6">
            <Skeleton variant="text" width="100%" height={48} />
            <Skeleton variant="text" width="80%" height={48} className="mx-auto" />
          </div>

          {/* Description */}
          <div className="mb-10">
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="90%" height={24} className="mx-auto mt-1" />
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Skeleton variant="text" width={180} height={48} className="rounded-lg" />
            <Skeleton variant="text" width={160} height={48} className="rounded-lg" />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Key facts grid skeleton
 */
export function KeyFactsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <section className="py-16 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={clsx(
            'grid gap-6',
            count === 4
              ? 'grid-cols-2 md:grid-cols-4'
              : 'grid-cols-2 md:grid-cols-3'
          )}
        >
          {Array.from({ length: count }).map((_, index) => (
            <div
              key={index}
              className="text-center p-6 bg-slate-900/50 border border-slate-800 rounded-xl"
            >
              <Skeleton variant="text" width={100} height={16} className="mx-auto mb-2" />
              <Skeleton variant="text" width={80} height={24} className="mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Events section skeleton for homepage
 */
export function EventsSectionSkeleton() {
  return (
    <section className="py-16 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <Skeleton variant="text" width={240} height={32} className="mb-2" />
            <Skeleton variant="text" width={320} height={20} />
          </div>
          <Skeleton variant="text" width={140} height={40} className="rounded-lg" />
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-800">
                <Skeleton variant="text" width={80} height={20} className="mb-2" />
                <Skeleton variant="text" width="90%" height={24} />
                <div className="mt-2 space-y-1">
                  <Skeleton variant="text" width={140} height={16} />
                  <Skeleton variant="text" width={80} height={16} />
                </div>
              </div>
              <div className="p-6">
                <TextSkeleton lines={3} lastLineWidth="60%" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Features grid skeleton
 */
export function FeaturesSkeleton({ count = 3 }: { count?: number }) {
  return (
    <section className="py-16 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Skeleton variant="text" width={300} height={32} className="mx-auto mb-4" />
          <Skeleton variant="text" width="60%" height={20} className="mx-auto" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="text-center p-6 bg-slate-900 border border-slate-800 rounded-xl">
              <Skeleton
                variant="circular"
                width={56}
                height={56}
                className="mx-auto mb-4"
              />
              <Skeleton variant="text" width={200} height={24} className="mx-auto mb-2" />
              <TextSkeleton lines={3} lastLineWidth="50%" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Contact form skeleton
 */
export function ContactFormSkeleton() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-800">
        <Skeleton variant="text" width={160} height={24} />
      </div>
      <div className="p-6 space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            <Skeleton variant="text" width={60} height={16} className="mb-2" />
            <Skeleton variant="text" width="100%" height={44} className="rounded-lg" />
          </div>
        ))}
        <div>
          <Skeleton variant="text" width={80} height={16} className="mb-2" />
          <Skeleton variant="rectangular" width="100%" height={120} className="rounded-lg" />
        </div>
        <Skeleton variant="text" width="100%" height={44} className="rounded-lg mt-4" />
      </div>
    </div>
  );
}

/**
 * Contact info cards skeleton
 */
export function ContactInfoSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-6">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-800 flex items-center gap-3">
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={120} height={24} />
          </div>
          <div className="p-6">
            <TextSkeleton lines={2} lastLineWidth="70%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default ContentSkeleton;
